import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import NotificationServ from '@api/Notifications/NotificationServ';
import { NotificationType } from '@api/Notifications/Notification';
import { ProfileTypes } from '@api/Users/User';
import { getProfileName } from '@utils/Profiles';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { hideLoader, showLoader } from '@utils/Loader';

/**
 * Enviar un aviso interno al staff, a mano.
 *
 * Convive con los emisores automáticos (pedido nuevo, pedido retrasado, caja sin
 * cerrar, stock en cero): esto es para lo que no tiene regla — "mañana llega el
 * proveedor a las 7", "hoy cerramos temprano".
 *
 * ⚠️ **Sin audiencia, lo ve TODO el staff.** Por eso la confirmación dice a
 * cuántos va antes de mandarlo: un aviso a todos no se puede "desenviar", y el
 * push ya salió al dispositivo de cada quien.
 */

// Todos los perfiles, en el orden del enum: un perfil nuevo aparece solo.
const PERFILES = Object.values(ProfileTypes);

const SendNotification: React.FunctionComponent = () => {
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const [title, setTitle] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [profiles, setProfiles] = useState<ProfileTypes[]>([]);
    const [branchOfficeIds, setBranchOfficeIds] = useState<string[]>([]);
    const [sending, setSending] = useState<boolean>(false);

    const nombreSucursal = useMemo(
        () => (id: string) => branchOffices?.find((branch: any) => branch.id === id)?.name || id,
        [branchOffices]
    );

    /** Frase que describe a quién le va a llegar, en español y sin jerga. */
    const descripcionAudiencia = useMemo(() => {
        const quien = profiles.length ? profiles.map(getProfileName).join(', ') : 'todo el staff';
        const donde = branchOfficeIds.length
            ? ` de ${branchOfficeIds.map(nombreSucursal).join(', ')}`
            : ' de todas las sucursales';
        return `${quien}${donde}`;
    }, [profiles, branchOfficeIds, nombreSucursal]);

    const limpiar = () => {
        setTitle('');
        setBody('');
        setProfiles([]);
        setBranchOfficeIds([]);
    };

    const enviar = async () => {
        setSending(true);
        showLoader();
        try {
            await NotificationServ.notify({
                title: title.trim(),
                body: body.trim() || undefined,
                type: NotificationType.System,
                audience: {
                    profiles: profiles.length ? profiles : undefined,
                    branchOfficeIds: branchOfficeIds.length ? branchOfficeIds : undefined
                }
            });
            showAlertMessage('Aviso enviado');
            limpiar();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
        setSending(false);
    };

    const confirmar = () => {
        if (!title.trim()) {
            showAlertError('Escribe un título');
            return;
        }
        showIOSAlert({
            title: 'Enviar aviso',
            message: `Le va a llegar a ${descripcionAudiencia}.\n\nUn aviso enviado no se puede retirar de los dispositivos.`,
            buttons: [
                { text: 'Enviar', onPress: enviar },
                { text: 'Cancelar', onPress: () => {}, style: 'cancel' }
            ]
        });
    };

    return (
        <Box height="100%" p={4} sx={{ overflowY: 'auto' }}>
            <Stack component={Card} boxShadow={4} borderRadius={4} p={4} spacing={3} maxWidth={720}>
                <Stack>
                    <Typography variant="h5" fontWeight={600}>
                        Enviar un aviso al staff
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Aparece en la campana de quien corresponda y, si tiene los avisos activados en su
                        dispositivo, le llega también en pantalla.
                    </Typography>
                </Stack>

                <TextField
                    label="Título"
                    placeholder="Mañana llega el proveedor a las 7"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    fullWidth
                    required
                />
                <TextField
                    label="Mensaje"
                    placeholder="Opcional: el detalle"
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                />

                <Divider />

                <Typography variant="subtitle2" fontWeight={600}>
                    ¿A quién?
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                        <InputLabel>Perfiles</InputLabel>
                        <Select
                            multiple
                            value={profiles}
                            onChange={(event) => setProfiles(event.target.value as ProfileTypes[])}
                            input={<OutlinedInput label="Perfiles" />}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {(selected as ProfileTypes[]).map((value) => (
                                        <Chip key={value} label={getProfileName(value)} size="small" />
                                    ))}
                                </Stack>
                            )}
                        >
                            {PERFILES.map((profile) => (
                                <MenuItem key={profile} value={profile}>
                                    {getProfileName(profile)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Sucursales</InputLabel>
                        <Select
                            multiple
                            value={branchOfficeIds}
                            onChange={(event) => setBranchOfficeIds(event.target.value as string[])}
                            input={<OutlinedInput label="Sucursales" />}
                            renderValue={(selected) => (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {(selected as string[]).map((value) => (
                                        <Chip key={value} label={nombreSucursal(value)} size="small" />
                                    ))}
                                </Stack>
                            )}
                        >
                            {(branchOffices || []).map((branch: any) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                {/* Dejar los dos filtros vacíos NO es un error, pero conviene que
                    se vea antes de mandarlo. */}
                <Alert severity={profiles.length || branchOfficeIds.length ? 'info' : 'warning'}>
                    Le llegará a <strong>{descripcionAudiencia}</strong>.
                </Alert>

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={confirmar}
                    disabled={sending || !title.trim()}
                >
                    Enviar aviso
                </Button>
            </Stack>
        </Box>
    );
};

export default SendNotification;
